import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const Logo = () => (
  <Link to="/" data-testid="nav-brand-logo" className="flex items-center shrink-0 rounded-lg bg-white px-3 py-1.5 shadow-sm">
    <img src="/assets/logo-light.png" alt="OMNIBÂT — Bâtir avec exigence, livrer avec confiance" className="h-8 lg:h-9 w-auto" />
  </Link>
);

const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useLanguage();
  return (
    <div className={`flex items-center border border-line ${className}`} data-testid="nav-language-toggle">
      {["fr", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}-button`}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1.5 font-mono text-[11px] uppercase tracking-widest transition-colors ${
            lang === l ? "bg-brand text-ink" : "text-fog hover:text-cream"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const Navbar = () => {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/poles", label: t.nav.poles },
    { to: "/realisations", label: t.nav.projects },
    { to: "/solutions", label: t.nav.solutions },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="main-navigation"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "border-b border-line bg-ink/85 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 lg:h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={({ isActive }) =>
                `font-mono text-[11px] uppercase tracking-[0.18em] transition-colors ${
                  isActive ? "text-brand" : "text-fog hover:text-cream"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <LangToggle />
          <Link
            to="/devis"
            data-testid="nav-quote-cta"
            className="ml-1 bg-brand px-5 py-2.5 font-display text-sm font-bold uppercase tracking-[0.12em] text-ink transition-colors hover:bg-brand-hover"
          >
            {t.nav.quote}
          </Link>
        </div>
        <button
          data-testid="mobile-menu-button"
          aria-label={open ? t.nav.close : t.nav.menu}
          onClick={() => setOpen(!open)}
          className="lg:hidden flex h-10 w-10 items-center justify-center border border-line text-cream"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-drawer"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden border-b border-line bg-ink/95 backdrop-blur-xl"
          >
            <div className="px-4 py-6 flex flex-col gap-1">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={l.to}
                    className={({ isActive }) =>
                      `block py-3 border-b border-line/60 font-display text-2xl font-semibold uppercase tracking-wide ${
                        isActive ? "text-brand" : "text-cream"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <div className="mt-6 flex items-center justify-between gap-3">
                <LangToggle />
              </div>
              <Link
                to="/devis"
                data-testid="mobile-quote-cta"
                className="mt-4 bg-brand px-5 py-4 text-center font-display text-lg font-bold uppercase tracking-[0.12em] text-ink"
              >
                {t.nav.quote}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
